<script setup lang="ts">
  defineOptions({ name: 'EventDetailAgendaSection' })

  import { computed, ref, type PropType } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { BookmarkIcon, CalendarPlus01Icon, FilterLinesIcon, MarkerPin01Icon, SearchLgIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'

  import CPAvatar from '@ui/components/CPAvatar.vue'
  import CPButton from '@ui/components/CPButton.vue'
  import CPCheckbox from '@ui/components/CPCheckbox.vue'
  import CPDivider from '@ui/components/CPDivider.vue'
  import CPInput from '@ui/components/CPInput.vue'
  import CPPopover from '@ui/components/CPPopover.vue'
  import CPSelect from '@ui/components/CPSelect.vue'
  import CPTag from '@ui/components/CPTag.vue'
  import CPText from '@ui/components/CPText.vue'
  import CPTooltip from '@ui/components/CPTooltip.vue'

  import type { EventDetailAgendaDay, EventDetailAgendaItem, EventDetailAgendaTagColor } from '../types/event-detail'

  const props = defineProps({
    summary: {
      type: String,
      required: true,
    },
    days: {
      type: Array as PropType<readonly EventDetailAgendaDay[]>,
      required: true,
    },
    isRegistered: {
      type: Boolean,
      required: true,
    },
    idPrefix: {
      type: String,
      default: 'event-detail',
    },
  })

  const emit = defineEmits<{
    saveSession: [sessionId: string]
    addToCalendar: [sessionId: string]
    openSpeaker: [speakerId: string]
  }>()

  const { t } = useI18n()
  const TAG_COLORS: Record<string, EventDetailAgendaTagColor> = {
    Keynote: 'blue-light',
    Workshop: 'purple',
    Networking: 'success',
    Sales: 'gray',
    Marketing: 'gray',
    Product: 'gray',
  }
  const DESCRIPTION_PREVIEW_CHARS = 160
  const searchQuery = ref('')
  const selectedLabels = ref<string[]>([])
  const expandedDescriptionIds = ref(new Set<string>())
  const activeDayId = ref(props.days[0]?.id ?? '')

  const dayOptions = computed(() =>
    props.days.map(day => {
      const count = day.sessions.length
      return {
        id: day.id,
        label: day.headingLabel ?? day.label,
        count,
        countLabel:
          count === 1
            ? t('clientportal.events.detail.agendaDaySessionsOne')
            : t('clientportal.events.detail.agendaDaySessionsMany', { count }),
      }
    })
  )

  const activeDay = computed(() => props.days.find(day => day.id === activeDayId.value) ?? props.days[0])

  const uniqueLabels = computed(() => {
    const seen = new Set<string>()
    const labels: string[] = []
    for (const session of activeDay.value?.sessions ?? []) {
      const label = session.tag?.trim()
      if (!label || seen.has(label)) continue
      seen.add(label)
      labels.push(label)
    }
    return labels
  })

  const hasLabelFilters = computed(() => uniqueLabels.value.length > 0)

  const daySelectOptions = computed(() =>
    dayOptions.value.map(day => ({
      value: day.id,
      label: `${day.label} · ${day.countLabel}`,
    }))
  )

  const filteredSessions = computed((): readonly EventDetailAgendaItem[] => {
    const sessions = activeDay.value?.sessions ?? []
    const allowed = new Set(uniqueLabels.value)
    const labels = new Set(selectedLabels.value.filter(label => allowed.has(label)))
    const byLabel = labels.size === 0 ? sessions : sessions.filter(session => Boolean(session.tag && labels.has(session.tag)))
    const query = searchQuery.value.trim().toLowerCase()
    if (!query) return byLabel
    return byLabel.filter(
      session =>
        session.title.toLowerCase().includes(query) ||
        session.meta.toLowerCase().includes(query) ||
        (session.description ?? '').toLowerCase().includes(query) ||
        (session.speakerName ?? '').toLowerCase().includes(query) ||
        (session.tag ?? '').toLowerCase().includes(query) ||
        (session.speakers ?? []).some(speaker => speaker.name.toLowerCase().includes(query) || speaker.role.toLowerCase().includes(query))
    )
  })

  const tagColor = (tag?: string): EventDetailAgendaTagColor => {
    if (!tag) return 'gray'
    return TAG_COLORS[tag] ?? 'blue-light'
  }

  const isSessionSaved = (session: EventDetailAgendaItem) => session.isSaved === true

  const sessionSaveLabel = (session: EventDetailAgendaItem) =>
    isSessionSaved(session) ? t('clientportal.events.detail.agendaSaved') : t('clientportal.events.detail.agendaSaveSchedule')

  const isDescriptionLong = (description?: string) => (description?.length ?? 0) > DESCRIPTION_PREVIEW_CHARS

  const isDescriptionExpanded = (sessionId: string) => expandedDescriptionIds.value.has(sessionId)

  const toggleDescription = (sessionId: string) => {
    const next = new Set(expandedDescriptionIds.value)
    if (next.has(sessionId)) next.delete(sessionId)
    else next.add(sessionId)
    expandedDescriptionIds.value = next
  }

  const requestSaveSession = (sessionId: string) => {
    emit('saveSession', sessionId)
  }

  const canAddToCalendar = (session: EventDetailAgendaItem) => Boolean(session.startAt && session.endAt && session.timeZone)

  const requestAddToCalendar = (sessionId: string) => {
    emit('addToCalendar', sessionId)
  }

  const requestOpenSpeaker = (speakerId: string) => {
    const id = speakerId.trim()
    if (!id) return
    emit('openSpeaker', id)
  }

  const selectDay = (dayId: string) => {
    activeDayId.value = dayId
  }

  const handleDaySelect = (value: string | number | boolean | unknown[] | Record<string, unknown>) => {
    selectDay(String(value))
  }

  const isLabelSelected = (label: string) => selectedLabels.value.includes(label)

  const setLabelSelected = (label: string, checked: boolean) => {
    if (checked) {
      if (!selectedLabels.value.includes(label)) selectedLabels.value = [...selectedLabels.value, label]
      return
    }
    selectedLabels.value = selectedLabels.value.filter(item => item !== label)
  }

  const clearLabelFilters = () => {
    selectedLabels.value = []
  }

  const labelFilterId = (label: string) =>
    createElementId(
      props.idPrefix,
      'agenda-filter',
      label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'label'
    )

  const sessionClockLabel = (time: string, meridiem?: string) => [time, meridiem].filter(Boolean).join(' ')

  const sessionEndLabel = (session: EventDetailAgendaItem) =>
    session.endTime ? sessionClockLabel(session.endTime, session.endMeridiem) : ''
</script>

<template>
  <section
    :id="createElementId(idPrefix, 'section', 'agenda')"
    class="flex scroll-mt-28 flex-col overflow-hidden rounded-xl bg-background shadow-sm"
  >
    <div class="flex flex-wrap items-start justify-between gap-3 px-5 py-4 md:px-6">
      <div class="min-w-0">
        <CPText :id="createElementId(idPrefix, 'agenda', 'title')" size="lg" weight="bold" html-tag="h2" class="text-body">
          {{ t('clientportal.events.detail.agendaTitle') }}
        </CPText>
        <CPText size="sm" weight="normal" class="mt-0.5 text-500">
          {{ summary }}
        </CPText>
      </div>

      <div class="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2 sm:max-w-lg">
        <CPInput
          :id="createElementId(idPrefix, 'agenda', 'search')"
          :model-value="searchQuery"
          :prefix-icon="SearchLgIcon"
          type="text"
          size="sm"
          clearable
          class="min-w-[8rem] flex-1"
          :placeholder="t('clientportal.events.detail.agendaSearch')"
          @update:model-value="searchQuery = String($event ?? '')"
        />
        <CPPopover
          v-if="hasLabelFilters"
          :id="createElementId(idPrefix, 'agenda', 'filters')"
          trigger="click"
          placement="bottom-end"
          :show-arrow="false"
        >
          <template #trigger>
            <CPButton :id="createElementId(idPrefix, 'agenda', 'filters-trigger')" type="secondary" color="secondary" size="sm">
              <template #iconLeft>
                <FilterLinesIcon class="h-4 w-4" aria-hidden="true" />
              </template>
              {{ t('clientportal.events.detail.agendaFilters') }}
            </CPButton>
          </template>
          <div class="flex min-w-[14rem] flex-col gap-2 p-3">
            <div class="flex items-center justify-between gap-2">
              <CPText size="sm" weight="semibold" class="text-body">
                {{ t('clientportal.events.detail.agendaFilterByLabel') }}
              </CPText>
              <CPButton
                :id="createElementId(idPrefix, 'agenda', 'filters-clear')"
                type="text"
                color="secondary"
                size="xs"
                @click="clearLabelFilters"
              >
                {{ t('clientportal.events.detail.agendaFilterClear') }}
              </CPButton>
            </div>
            <div :id="createElementId(idPrefix, 'agenda', 'filters-options')" class="flex max-h-60 flex-col gap-2 overflow-y-auto">
              <CPCheckbox
                v-for="label in uniqueLabels"
                :id="labelFilterId(label)"
                :key="label"
                :label="label"
                :checked="isLabelSelected(label)"
                size="sm"
                @update:checked="checked => setLabelSelected(label, checked)"
              />
            </div>
          </div>
        </CPPopover>
      </div>
    </div>

    <CPDivider margin-top="0px" margin-bottom="0px" />

    <div class="grid grid-cols-1 lg:grid-cols-[13.75rem_minmax(0,1fr)]">
      <aside :id="createElementId(idPrefix, 'agenda', 'day-rail')" class="hidden flex-col gap-2 p-4 lg:flex">
        <CPText size="sm" weight="semibold" class="px-3 pb-2 uppercase tracking-[0.04em] text-500">
          {{ t('clientportal.events.detail.agendaDays') }}
        </CPText>
        <CPButton
          v-for="day in dayOptions"
          :id="createElementId(idPrefix, 'agenda-day', day.id)"
          :key="day.id"
          type="secondary"
          :color="day.id === activeDay?.id ? 'custom' : 'transparent'"
          size="sm"
          class="agenda-day-button w-full text-left"
          :custom-background-color="day.id === activeDay?.id ? 'var(--cp-primary-50)' : undefined"
          :custom-text-color="day.id === activeDay?.id ? 'var(--cp-primary-700)' : undefined"
          :custom-border-color="day.id === activeDay?.id ? 'transparent' : 'transparent'"
          :custom-hover-background-color="day.id === activeDay?.id ? 'var(--cp-primary-100)' : 'var(--cp-surface-100)'"
          @click="selectDay(day.id)"
        >
          <span class="flex w-full flex-col items-start gap-0.5 text-left">
            <CPText size="md" weight="semibold">{{ day.label }}</CPText>
            <CPText size="md" weight="normal" class="text-500">{{ day.countLabel }}</CPText>
          </span>
        </CPButton>
      </aside>

      <div class="px-4 py-4 md:px-6 md:pb-6">
        <div class="pb-3 lg:hidden">
          <CPSelect
            :id="createElementId(idPrefix, 'agenda', 'day-select')"
            :model-value="activeDayId"
            :options="daySelectOptions"
            full-width
            size="sm"
            :placeholder="t('clientportal.events.detail.agendaDays')"
            :title="t('clientportal.events.detail.agendaDays')"
            @update:value="handleDaySelect"
          />
        </div>
        <div class="flex flex-col gap-3">
          <div
            v-for="session in filteredSessions"
            :id="createElementId(idPrefix, 'agenda-session', session.id)"
            :key="session.id"
            class="relative flex flex-col gap-3 rounded-[10px] border border-solid border-[var(--cp-border-200)] bg-background p-4 sm:flex-row sm:items-start sm:gap-4"
            :class="isRegistered ? 'pr-14 sm:pr-4' : undefined"
          >
            <div :id="createElementId(idPrefix, 'agenda-session-meta', session.id)" class="w-full shrink-0 sm:w-[9.75rem]">
              <CPText
                :id="createElementId(idPrefix, 'agenda-start', session.id)"
                size="md"
                weight="semibold"
                html-tag="div"
                class="tabular-nums text-body"
              >
                {{ sessionClockLabel(session.time, session.meridiem) }}
              </CPText>
              <CPText
                v-if="sessionEndLabel(session)"
                :id="createElementId(idPrefix, 'agenda-end', session.id)"
                size="sm"
                weight="normal"
                html-tag="div"
                class="mt-0.5 text-500"
              >
                {{ t('clientportal.events.detail.agendaTimeTo', { time: sessionEndLabel(session) }) }}
              </CPText>
              <span v-if="session.meta" class="mt-1 inline-flex min-w-0 items-center gap-1.5 text-500">
                <MarkerPin01Icon class="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <CPText size="sm" weight="normal" class="truncate">{{ session.meta }}</CPText>
              </span>
            </div>

            <div :id="createElementId(idPrefix, 'agenda-session-body', session.id)" class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2">
                <CPText
                  :id="createElementId(idPrefix, 'agenda-session-title', session.id)"
                  size="lg"
                  weight="semibold"
                  class="text-pretty text-body"
                >
                  {{ session.title }}
                </CPText>
                <CPTag
                  v-if="session.tag"
                  :id="createElementId(idPrefix, 'agenda-tag', session.id)"
                  size="xs"
                  :color="tagColor(session.tag)"
                  :round="true"
                  :bordered="false"
                  :interactive="false"
                >
                  <CPText size="xs" weight="semibold">{{ session.tag }}</CPText>
                </CPTag>
              </div>

              <div v-if="session.description" class="mt-1.5 flex min-w-0 flex-col items-start gap-1">
                <CPText
                  :id="createElementId(idPrefix, 'agenda-description', session.id)"
                  size="sm"
                  weight="normal"
                  :class="[
                    'max-w-[70ch] text-pretty text-600',
                    { 'agenda-description-clamp': isDescriptionLong(session.description) && !isDescriptionExpanded(session.id) },
                  ]"
                >
                  {{ session.description }}
                </CPText>
                <CPButton
                  v-if="isDescriptionLong(session.description)"
                  :id="createElementId(idPrefix, 'agenda-description-toggle', session.id)"
                  type="text"
                  color="secondary"
                  size="xs"
                  class="!px-0"
                  @click="toggleDescription(session.id)"
                >
                  {{
                    isDescriptionExpanded(session.id)
                      ? t('clientportal.events.detail.agendaReadLess')
                      : t('clientportal.events.detail.agendaReadMore')
                  }}
                </CPButton>
              </div>

              <div
                v-if="session.speakers?.length"
                :id="createElementId(idPrefix, 'agenda-speakers', session.id)"
                class="mt-2 flex flex-col gap-2 pt-0.5 lg:flex-row lg:flex-wrap lg:items-center lg:gap-x-6 lg:gap-y-2"
              >
                <div
                  v-for="speaker in session.speakers"
                  :id="createElementId(idPrefix, 'agenda-speaker', speaker.id)"
                  :key="speaker.id"
                  role="button"
                  tabindex="0"
                  class="flex min-w-0 cursor-pointer items-center gap-2 lg:max-w-[15rem]"
                  :aria-label="t('clientportal.events.detail.speakersOpenDetails', { name: speaker.name })"
                  @click="requestOpenSpeaker(speaker.id)"
                  @keydown.enter.prevent="requestOpenSpeaker(speaker.id)"
                  @keydown.space.prevent="requestOpenSpeaker(speaker.id)"
                >
                  <CPAvatar
                    :title="speaker.name"
                    :src="speaker.imageUrl"
                    :size="30"
                    shape="square"
                    source="client-portal"
                    :use-raw-url="true"
                    class="shrink-0"
                  />
                  <span class="flex min-w-0 flex-col">
                    <CPText size="sm" weight="semibold" class="truncate text-body">
                      {{ speaker.name }}
                    </CPText>
                    <CPText v-if="speaker.role" size="xs" weight="normal" class="truncate text-500">
                      {{ speaker.role }}
                    </CPText>
                  </span>
                </div>
              </div>
            </div>

            <div
              v-if="isRegistered"
              :id="createElementId(idPrefix, 'agenda-session-actions', session.id)"
              class="absolute right-4 top-4 flex shrink-0 gap-1 sm:static"
            >
              <CPTooltip
                v-if="canAddToCalendar(session)"
                :id="createElementId(idPrefix, 'agenda-calendar-tooltip', session.id)"
                trigger="hover"
                placement="top"
                to="body"
              >
                <template #trigger>
                  <CPButton
                    :id="createElementId(idPrefix, 'agenda-calendar', session.id)"
                    type="secondary"
                    color="secondary"
                    size="xs"
                    :aria-label="t('clientportal.events.detail.agendaAddToCalendar')"
                    @click="requestAddToCalendar(session.id)"
                  >
                    <CalendarPlus01Icon class="h-3.5 w-3.5" aria-hidden="true" />
                  </CPButton>
                </template>
                <CPText size="xs">{{ t('clientportal.events.detail.agendaAddToCalendar') }}</CPText>
              </CPTooltip>
              <CPTooltip :id="createElementId(idPrefix, 'agenda-save-tooltip', session.id)" trigger="hover" placement="top" to="body">
                <template #trigger>
                  <CPButton
                    :id="createElementId(idPrefix, 'agenda-save', session.id)"
                    :type="isSessionSaved(session) ? 'primary' : 'secondary'"
                    :color="isSessionSaved(session) ? 'primary' : 'secondary'"
                    size="xs"
                    :aria-label="sessionSaveLabel(session)"
                    @click="requestSaveSession(session.id)"
                  >
                    <BookmarkIcon class="h-3.5 w-3.5" aria-hidden="true" />
                  </CPButton>
                </template>
                <CPText size="xs">{{ sessionSaveLabel(session) }}</CPText>
              </CPTooltip>
            </div>
          </div>

          <CPText v-if="filteredSessions.length === 0" size="md" weight="normal" class="py-8 text-center text-500">
            {{ t('clientportal.events.detail.agendaEmptySearch') }}
          </CPText>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
  .agenda-day-button {
    --n-height: auto !important;
    --n-padding: 0.5rem 0.75rem !important;
    --n-border: none !important;
    --n-border-hover: none !important;
    --n-border-pressed: none !important;
    --n-border-focus: none !important;
    --n-box-shadow: none !important;
    height: auto;
    width: 100%;
    text-align: left;
  }

  .agenda-description-clamp {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    overflow: hidden;
  }
</style>
